/* eslint-disable no-use-before-define */
/* The kiosk camera loop. States: idle -> challenge -> submit -> result -> (leave | idle).
 *
 * The browser only MEASURES (eyes, head pose, smile, face-crop quality, 128-D descriptors).
 * The SERVER decides: it re-checks the liveness evidence, matches the descriptors and records (or, in test
 * mode, only logs) the punch.
 *
 * createKiosk({ root, token, onToken, onAuthLost }) -> { start(), stop(), openSetup() }
 * `root` is the element that contains the markup rendered by Kiosk.js (elements are found by data-k="...").
 */
import * as LC from './engine/liveness-core';
import { FaceEngine, ProbeCollector, browserEnv } from './engine/faceengine';
import { loadFaceApi, MODEL_URL } from './faceApi';
import { BASE } from './hrApi';

export function createKiosk({ root, token: initialToken, onToken, onAuthLost, onSignOut }) {
  const $ = (id) => root.querySelector(`[data-k="${id}"]`);
  let token = initialToken;
  let stopped = false;
  const timers = [];
  const listeners = [];
  let stream = null;
  let wakeLock = null;

  const store = {
    get(k, d) { try { const v = localStorage.getItem(k); return v === null ? d : v; } catch (_) { return d; } },
    set(k, v) { try { localStorage.setItem(k, v); } catch (_) { /* private mode */ } },
  };
  const opts = {
    sound: store.get('fa_sound', '1') === '1',
    speak: store.get('fa_speak', '0') === '1',
    debug: store.get('fa_debug', '0') === '1' || /[?&]debug=1/.test(window.location.search),
  };

  const K = {
    cfg: null, engine: null, video: $('video'), canvas: $('overlay'), running: false, mode: 'boot',
    att: null, okSince: 0, leaveSince: 0, fps: 0, lastAttrs: null, netDown: false, paused: false, faceapi: null,
  };
  const delay = (ms) => new Promise((r) => setTimeout(r, ms));
  const euclid = (a, b) => { let t = 0; for (let i = 0; i < a.length; i++) { const d = a[i] - b[i]; t += d * d; } return Math.sqrt(t); };
  const NEW_PERSON = 0.55; // distance above this = somebody else (same person ~0.2, different people >= 0.5)
  const now = () => performance.now();
  const on = (target, ev, fn, o) => { target.addEventListener(ev, fn, o); listeners.push(() => target.removeEventListener(ev, fn, o)); };

  // ---- network ---------------------------------------------------------------------------------------
  class NetErr extends Error { constructor(status, body, msg) { super(msg || (body && body.error) || 'Request failed'); this.status = status; this.body = body || {}; } }
  async function call(path, body) {
    let res;
    try {
      res = await fetch(`${BASE}/face/kiosk${path}`, {
        method: body === undefined ? 'GET' : 'POST',
        headers: Object.assign({ Authorization: `Bearer ${token}` }, body === undefined ? {} : { 'Content-Type': 'application/json' }),
        body: body === undefined ? undefined : JSON.stringify(body),
      });
    } catch (_) { throw new NetErr(0, {}, 'Cannot reach the server'); }
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new NetErr(res.status, data);
    if (data && data.renewedToken) { token = data.renewedToken; if (onToken) onToken(token); }
    return data;
  }
  const authLost = (msg) => { stopped = true; K.running = false; cleanup(); if (onAuthLost) onAuthLost(msg || 'This device was signed out. Sign in again.'); };

  // ---- sound & speech --------------------------------------------------------------------------------
  let audio = null;
  function beep(freqs, dur = 0.11, type = 'sine', vol = 0.18) {
    if (!opts.sound) return;
    try {
      audio = audio || new (window.AudioContext || window.webkitAudioContext)();
      if (audio.state === 'suspended') audio.resume();
      let t = audio.currentTime;
      for (const f of freqs) {
        const o = audio.createOscillator(), g = audio.createGain();
        o.type = type; o.frequency.value = f;
        g.gain.setValueAtTime(0.0001, t); g.gain.exponentialRampToValueAtTime(vol, t + 0.01); g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
        o.connect(g).connect(audio.destination); o.start(t); o.stop(t + dur + 0.02);
        t += dur * 0.9;
      }
    } catch (_) { /* audio is optional */ }
  }
  const sounds = {
    tick: () => beep([880], 0.07), ok: () => beep([660, 990], 0.12), warn: () => beep([520, 520], 0.12, 'triangle'), bad: () => beep([240, 180], 0.2, 'sawtooth', 0.12),
  };
  function speak(text) {
    if (!opts.speak || !('speechSynthesis' in window)) return;
    try { window.speechSynthesis.cancel(); window.speechSynthesis.speak(new SpeechSynthesisUtterance(text)); } catch (_) { /* optional */ }
  }
  on(window, 'pointerdown', () => { try { audio = audio || new (window.AudioContext || window.webkitAudioContext)(); audio.resume(); } catch (_) { /* ignore */ } }, { once: true });

  // ---- UI helpers ------------------------------------------------------------------------------------
  const ui = {
    mode(m, tone = '') { $('stage').dataset.mode = m; $('stage').dataset.tone = tone; },
    say(prompt, sub = '') { if ($('prompt').textContent !== prompt) $('prompt').textContent = prompt; if ($('sub').textContent !== sub) $('sub').textContent = sub; },
    ring(frac) { $('ring-fill').style.strokeDasharray = `${Math.max(0, Math.min(1, frac)) * 100} 100`; },
    banner(msg) { const b = $('banner'); b.textContent = msg || ''; b.style.display = msg ? 'block' : 'none'; },
    shadow(on_) { $('shadowbar').style.display = on_ ? 'block' : 'none'; },
    steps(list, done, current) {
      const label = { blink: 'Blink', turn_left: 'Turn left', turn_right: 'Turn right', smile: 'Smile' };
      const el = $('steps'); el.textContent = '';
      list.forEach((s, i) => {
        const chip = document.createElement('span');
        chip.className = `fk-chip ${i < done ? 'done' : i === current ? 'now' : ''}`;
        chip.textContent = `${i < done ? '✓ ' : ''}${label[s] || s}`;
        el.appendChild(chip);
      });
    },
  };
  const PROMPT = {
    blink: ['Blink once', 'Look straight at the camera, then blink'],
    turn_left: ['Turn your head to your left', 'Then look back at the camera'],
    turn_right: ['Turn your head to your right', 'Then look back at the camera'],
    smile: ['Give a smile', 'Relax your face first, then smile'],
  };

  function tickClock() {
    if (!K.cfg) return;
    const d = new Date();
    $('time').textContent = new Intl.DateTimeFormat('en-GB', { timeZone: K.cfg.tz, hour: '2-digit', minute: '2-digit', hour12: false }).format(d);
    $('date').textContent = new Intl.DateTimeFormat('en-GB', { timeZone: K.cfg.tz, weekday: 'long', day: 'numeric', month: 'long' }).format(d);
  }

  // ---- camera ----------------------------------------------------------------------------------------
  async function startCamera() {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) throw new Error('This browser cannot use the camera. The kiosk must be opened over HTTPS.');
    stream = await navigator.mediaDevices.getUserMedia({ video: { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: 'user', frameRate: { ideal: 30 } }, audio: false });
    K.video.srcObject = stream;
    await new Promise((res) => { if (K.video.readyState >= 2 && K.video.videoWidth) res(); else K.video.addEventListener('loadeddata', res, { once: true }); });
    await K.video.play().catch(() => {});
    stream.getVideoTracks()[0].addEventListener('ended', () => { ui.banner('Camera disconnected. Reconnect it, then reload this page.'); K.running = false; });
  }
  async function keepAwake() {
    try {
      if ('wakeLock' in navigator && !stopped) {
        wakeLock = await navigator.wakeLock.request('screen');
        wakeLock.addEventListener('release', () => { if (!stopped) timers.push(setTimeout(keepAwake, 1000)); });
      }
    } catch (_) { /* not supported */ }
  }

  // ---- debug overlay ---------------------------------------------------------------------------------
  const EDGES = [[0, 16], [17, 21], [22, 26], [27, 30], [30, 35], [36, 41], [42, 47], [48, 59], [60, 67]];
  function draw(faces, main) {
    const c = K.canvas, v = K.video;
    if (!opts.debug) { if (c.width) { c.width = 0; } return; }
    const cw = c.clientWidth, ch = c.clientHeight;
    if (c.width !== cw || c.height !== ch) { c.width = cw; c.height = ch; }
    const g = c.getContext('2d'); g.clearRect(0, 0, cw, ch);
    if (!faces || !faces.length) return;
    const s = Math.max(cw / v.videoWidth, ch / v.videoHeight);
    const ox = (cw - v.videoWidth * s) / 2, oy = (ch - v.videoHeight * s) / 2;
    const X = (x) => x * s + ox, Y = (y) => y * s + oy;
    for (const f of faces) {
      const isMain = f === main;
      g.strokeStyle = isMain ? '#ff6b6b' : 'rgba(255,255,255,.4)'; g.lineWidth = isMain ? 2 : 1;
      g.strokeRect(X(f.box.x), Y(f.box.y), f.box.w * s, f.box.h * s);
      if (!isMain) continue;
      g.fillStyle = '#ffd0d0';
      for (const p of f.landmarks) { g.beginPath(); g.arc(X(p.x), Y(p.y), 1.8, 0, 6.3); g.fill(); }
      g.strokeStyle = 'rgba(255,140,140,.55)'; g.lineWidth = 1;
      for (const [a, b] of EDGES) { g.beginPath(); for (let i = a; i <= b; i++) { const p = f.landmarks[i]; if (i === a) g.moveTo(X(p.x), Y(p.y)); else g.lineTo(X(p.x), Y(p.y)); } g.stroke(); }
    }
  }
  function hud(r, extra = '') {
    if (!opts.debug) { $('hud').style.display = 'none'; return; }
    $('hud').style.display = 'block';
    const m = r && r.main;
    const a = K.lastAttrs;
    const lines = [
      `state    ${K.mode}${K.att ? '  step ' + K.att.stepIdx + '/' + K.att.steps.length + '  probes ' + K.att.col.count() : ''}`,
      `fps      ${K.fps.toFixed(1)}   ${K.faceapi.tf.getBackend()}   detector ${r ? r.detector : '-'}   ${r ? r.ms.toFixed(0) : '-'} ms   video ${r && r.video ? r.video.w + 'x' + r.video.h : '-'}`,
      m ? `faces    ${r.count}   score ${m.score}   width ${(m.widthRatio * 100).toFixed(0)}% of frame` : 'faces    0',
      m ? `pose     yaw ${m.yaw}  pitch ${m.pitch}  roll ${m.roll}°` : '',
      m ? `eyes     EAR ${m.ear}   smile ${m.smile ?? '-'}` : '',
      m && m.sharp !== undefined ? `quality  sharp ${m.sharp}   brightness ${m.bright}` : '',
      a ? `profile  ${a.gender} (${(a.genderProb * 100).toFixed(0)}%)  age ~${a.age}  mostly ${a.emotion}` : '',
      extra,
    ].filter(Boolean);
    $('hud').textContent = lines.join('\n');
  }

  // ---- snapshot --------------------------------------------------------------------------------------
  const snapCanvas = document.createElement('canvas');
  function grab() {
    const v = K.video, w = 320, h = Math.round((w * v.videoHeight) / v.videoWidth);
    snapCanvas.width = w; snapCanvas.height = h;
    snapCanvas.getContext('2d').drawImage(v, 0, 0, w, h);
    return snapCanvas.toDataURL('image/jpeg', 0.62);
  }

  // ---- the attempt -----------------------------------------------------------------------------------
  const faceOk = (m) => m && m.score >= K.cfg.quality.minDetectionScore && m.widthRatio >= K.cfg.quality.minFaceWidthRatio;

  function frameOf(m, t, count) {
    const r3 = (x) => Math.round(x * 1000) / 1000;
    return { t: Math.round(t), ear: r3(m.ear), yaw: r3(m.yaw), sm: m.smile === null ? null : r3(m.smile), cx: Math.round(m.cx), cy: Math.round(m.cy), w: Math.round(m.box.w), h: Math.round(m.box.h), n: count };
  }

  async function beginAttempt() {
    const required = K.cfg.liveness.required;
    let ch = { required: false, steps: [], token: null, ttlSeconds: 40 };
    if (required) ch = await call('/challenge', {});
    const att = {
      token: ch.token, steps: ch.steps || [], ttl: ch.ttlSeconds || 40, t0: now(), series: [], stepIdx: 0, from: 0,
      col: new ProbeCollector(K.cfg.quality, { max: 7, minGapMs: required ? 230 : 140, frontalYaw: K.cfg.liveness.options.frontalYaw }),
      doneAt: null, lastFaceAt: now(), bestRank: -1, snap: null,
    };
    att.target = Math.min(5, Math.max(K.cfg.quality.minProbes + 1, 4));
    K.att = att; K.mode = 'challenge';
    ui.mode('challenge'); ui.ring(0.04); ui.steps(att.steps, 0, 0); beep([740], 0.06);
    if (att.steps.length) ui.say(...PROMPT[att.steps[0]]); else ui.say('Look straight at the camera', 'Hold still for a moment');
  }

  function progress(att) {
    const total = att.steps.length + 1;
    const col = Math.min(1, att.col.count() / att.target);
    return (att.stepIdx + (att.stepIdx >= att.steps.length ? col : 0)) / total;
  }

  async function challengeStep() {
    const att = K.att, cfg = K.cfg;
    const stepsDone = att.stepIdx >= att.steps.length;
    const r = await K.engine.analyze(K.video, {
      expressions: true, quality: true,
      descriptor: (face, vs) => att.col.wants(face, vs.w, now() - att.t0),
    });
    draw(r.faces, r.main); hud(r);
    const tNow = now() - att.t0;
    if (tNow > (att.ttl - 2.5) * 1000) return abort('Time ran out', 'Please try again');
    if (!r.main) {
      if (now() - att.lastFaceAt > 1000) return abort('Face lost', 'Stay in front of the camera and try again');
      return;
    }
    if (r.count > 1) return abort('One person at a time', 'Please step up on your own');
    att.lastFaceAt = now();
    const m = r.main;
    att.series.push(frameOf(m, tNow, 1));
    if (att.series.length > 850) return abort('Too slow', 'Please try again');

    if (!faceOk(m)) ui.say(m.widthRatio < cfg.quality.minFaceWidthRatio ? 'Come a little closer' : 'Face not clear', '');
    if (!stepsDone) {
      const type = att.steps[att.stepIdx];
      const idx = LC.stepProgress(type, att.series, att.from, cfg.liveness.options);
      if (idx >= 0) {
        att.stepIdx++; att.from = idx; sounds.tick();
        ui.steps(att.steps, att.stepIdx, att.stepIdx);
        if (att.stepIdx < att.steps.length) ui.say(...PROMPT[att.steps[att.stepIdx]]);
        else ui.say('Look straight at the camera', 'Hold still for a moment');
      } else if (faceOk(m)) ui.say(...PROMPT[type]);
    } else {
      if (att.doneAt === null) att.doneAt = tNow;
      const issues = att.col.issues(m, r.video.w);
      if (issues.length && att.col.count() < att.target) ui.say(issues[0][0].toUpperCase() + issues[0].slice(1), 'Hold still for a moment');
      else if (!issues.length) ui.say('Look straight at the camera', 'Hold still for a moment');
    }
    if (m.descriptor) {
      att.col.add(m, r.video.w, tNow);
      const rank = att.col.rank(att.col.meta(m, r.video.w, tNow));
      if (cfg.storeSnapshots && rank > att.bestRank) { att.bestRank = rank; att.snap = grab(); }
    }
    ui.ring(progress(att));

    const done = att.stepIdx >= att.steps.length;
    if (done && att.col.count() >= att.target) return submit();
    if (done && att.doneAt !== null && tNow - att.doneAt > 4500 && att.col.count() >= cfg.quality.minProbes) return submit();
    if (done && att.doneAt !== null && tNow - att.doneAt > 6000) {
      const issues = att.col.issues(m, r.video.w);
      return abort(issues.length ? issues[0][0].toUpperCase() + issues[0].slice(1) : 'Could not get a clear photo', 'Please try again');
    }
    return undefined;
  }

  async function submit() {
    const att = K.att; K.mode = 'submit';
    ui.say('Checking…', ''); ui.ring(1);
    if (K.cfg.liveness.required) {
      const ev = LC.evaluateChallenge(att.series, att.steps, K.cfg.liveness.options);
      if (!ev.ok) return abort('Could not confirm the movement', 'Please try again');
    }
    let attributes = null;
    if (K.cfg.storeAttributes || opts.debug) { try { attributes = await K.engine.attributes(K.video); if (attributes) K.lastAttrs = attributes; } catch (_) { /* optional */ } }
    const body = { token: att.token, series: K.cfg.liveness.required ? att.series : undefined, probes: att.col.export() };
    if (K.cfg.storeSnapshots && att.snap) body.snapshot = att.snap;
    if (K.cfg.storeAttributes && attributes) body.attributes = attributes;
    let res;
    try { res = await call('/recognize', body); K.netDown = false; ui.banner(K.slowNote || ''); }
    catch (e) { return handleNetError(e); }
    if (res.ok) { const ex = att.col.export(); K.lastDesc = ex.length ? ex[Math.floor(ex.length / 2)].descriptor : null; }
    if (typeof res.shadow === 'boolean') ui.shadow(res.shadow);
    return showResult(res);
  }

  function handleNetError(e) {
    K.att = null;
    if (e.status === 401) { authLost(e.body.error || 'This device was signed out. Sign in again.'); return undefined; }
    if (e.status === 403) { authLost(e.body.error || 'This device has been disabled.'); return undefined; }
    K.netDown = e.status === 0;
    if (e.status === 429) return showResult({ ok: false, outcome: 'error', message: 'Too many attempts — wait a moment' });
    if (e.status === 503) return showResult({ ok: false, outcome: 'error', message: 'Face recognition is not set up on the server yet' });
    return showResult({ ok: false, outcome: 'error', message: e.status === 0 ? 'Cannot reach the server' : (e.message || 'Something went wrong') });
  }

  function abort(prompt, sub) {
    K.att = null; K.mode = 'cooldown'; ui.mode('idle', 'warn'); ui.steps([], 0, 0); ui.ring(0);
    ui.say(prompt, sub); sounds.warn();
    K.until = now() + 2200; K.after = 'idle';
  }

  function showResult(res) {
    const tone = res.ok ? (res.duplicate ? 'warn' : 'ok') : (res.outcome === 'unknown' || res.outcome === 'spoof' || res.outcome === 'inactive' ? 'bad' : 'warn');
    K.att = null; K.mode = 'result';
    ui.mode('result', tone); ui.ring(1); ui.steps([], 0, 0); ui.say('', '');
    const late = $('res-late'); late.hidden = true;
    if (res.ok && res.employee) {
      const p = res.punch || {};
      $('res-kind').textContent = res.duplicate ? 'Already recorded' : (p.type === 'out' ? 'Checked out' : 'Checked in');
      $('res-name').textContent = res.employee.name;
      const base = res.duplicate ? [res.message, res.info].filter(Boolean).join('  ·  ') : `${p.local}${res.employee.department ? '  ·  ' + res.employee.department : ''}`;
      $('res-meta').textContent = res.shadow && !res.duplicate ? `${base}  ·  test mode, not recorded` : base;
      if (!res.duplicate && p.type === 'in' && p.is_late) { late.textContent = `${p.late_minutes} min late`; late.hidden = false; }
      if (!res.duplicate && p.type === 'out' && p.early_minutes > 0) { late.textContent = `${p.early_minutes} min early`; late.hidden = false; }
      if (res.duplicate) sounds.warn(); else sounds.ok();
      if (!res.duplicate) speak(res.message);
      K.until = now() + (res.duplicate ? 2600 : 2800); K.after = 'leave'; K.leaveSince = 0; K.leaveCheckAt = 0;
    } else {
      $('res-kind').textContent = res.outcome === 'unknown' ? 'Not recognised' : res.outcome === 'spoof' ? 'Check failed' : res.outcome === 'inactive' ? 'Not active' : 'Try again';
      $('res-name').textContent = res.message || 'Please try again';
      $('res-meta').textContent = res.outcome === 'unknown' ? 'Ask HR if your face has been enrolled' : '';
      sounds.bad();
      K.until = now() + 3000; K.after = 'idle';
    }
  }

  // ---- idle / leave ----------------------------------------------------------------------------------
  function toIdle() { K.mode = 'idle'; K.leaveSince = 0; K.lastDesc = null; ui.mode('idle'); ui.ring(0); ui.say('Look at the camera', ''); }

  async function idleStep() {
    const q = K.cfg.quality;
    const r = await K.engine.analyze(K.video, { search: true });
    draw(r.faces, r.main); hud(r);
    const m = r.main;
    if (K.mode === 'leave') {
      if (!m) { if (!K.leaveSince) K.leaveSince = now(); if (now() - K.leaveSince > 700) { toIdle(); } else ui.say('Thank you', ''); return; }
      K.leaveSince = 0;
      if (K.lastDesc) {
        if (now() - (K.leaveCheckAt || 0) < 350 || !faceOk(m)) { ui.say('Thank you', ''); return; }
        K.leaveCheckAt = now();
        let d = null;
        try { const full = await K.engine.analyze(K.video, { quality: false, descriptor: true }); if (full.main && full.main.descriptor) d = euclid(full.main.descriptor, K.lastDesc); } catch (_) { /* try next frame */ }
        if (d === null || d < NEW_PERSON) { ui.say('Thank you', ''); return; } // same person (or unsure): stay quiet, never re-punch
      }
      toIdle(); // a different face: go straight on
    }
    if (!m) { K.okSince = 0; ui.say('Look at the camera', 'Stand about an arm’s length away'); return; }
    if (r.count > 1) { K.okSince = 0; ui.say('One person at a time', ''); return; }
    if (!faceOk(m)) { K.okSince = 0; ui.say(m.widthRatio < q.minFaceWidthRatio ? 'Come a little closer' : 'Look at the camera', ''); return; }
    if (!K.okSince) K.okSince = now();
    ui.say('Hold still…', '');
    if (now() - K.okSince > 350) {
      K.okSince = 0;
      try { await beginAttempt(); K.netDown = false; ui.banner(K.slowNote || ''); } catch (e) { handleNetError(e); }
    }
  }

  // ---- main loop -------------------------------------------------------------------------------------
  async function loop() {
    let last = now();
    while (K.running && !stopped) {
      if (K.paused) { await delay(200); continue; }
      const t0 = now();
      try {
        if (K.mode === 'challenge') await challengeStep();
        else if (K.mode === 'idle' || K.mode === 'leave') await idleStep();
        else if (K.mode === 'result' || K.mode === 'cooldown') {
          if (now() > K.until) {
            K.mode = K.after; ui.mode('idle'); ui.ring(0); K.leaveSince = 0; K.okSince = 0;
            if (K.after === 'idle') ui.say('Look at the camera', '');
          }
          if (K.mode === 'result' || K.mode === 'cooldown') { draw(null); await delay(100); }
        } else await delay(100);
      } catch (e) {
        console.error('[kiosk]', e);
        if (K.mode === 'challenge') abort('Something went wrong', 'Please try again'); else await delay(500);
      }
      const dt = now() - last; last = now();
      K.fps = K.fps * 0.85 + (1000 / Math.max(1, dt)) * 0.15;
      if (K.mode === 'idle' || K.mode === 'leave') { const spent = now() - t0; if (spent < 160) await delay(160 - spent); } // ~6 fps while nobody is being checked
      else await new Promise((r) => requestAnimationFrame(r));
    }
  }

  // ---- setup dialog (S key, or long-press the device name) --------------------------------------------
  function openSetup() {
    $('opt-sound').checked = opts.sound; $('opt-speak').checked = opts.speak; $('opt-debug').checked = opts.debug;
    $('setup').classList.add('open');
    K.paused = true;
  }
  function closeSetup() { $('setup').classList.remove('open'); K.paused = false; }
  on($('setup-cancel'), 'click', closeSetup);
  on($('setup-go'), 'click', () => {
    opts.sound = $('opt-sound').checked; opts.speak = $('opt-speak').checked; opts.debug = $('opt-debug').checked;
    store.set('fa_sound', opts.sound ? '1' : '0'); store.set('fa_speak', opts.speak ? '1' : '0'); store.set('fa_debug', opts.debug ? '1' : '0');
    if (!opts.debug) { $('hud').style.display = 'none'; }
    closeSetup();
  });
  on($('setup-signout'), 'click', () => { stopped = true; K.running = false; cleanup(); if (onSignOut) onSignOut(); });
  on(window, 'keydown', (e) => {
    if ($('setup').classList.contains('open')) return;
    if (e.key === 'd') { opts.debug = !opts.debug; store.set('fa_debug', opts.debug ? '1' : '0'); }
    if (e.key === 's') openSetup();
    if (e.key === 'f' && document.documentElement.requestFullscreen) document.documentElement.requestFullscreen().catch(() => {});
  });
  let pressTimer = null;
  on($('where'), 'pointerdown', () => { pressTimer = setTimeout(() => openSetup(), 1500); });
  ['pointerup', 'pointerleave', 'pointercancel'].forEach((ev) => on($('where'), ev, () => clearTimeout(pressTimer)));

  function cleanup() {
    timers.forEach((t) => { clearTimeout(t); clearInterval(t); });
    listeners.forEach((off) => off());
    try { if (stream) stream.getTracks().forEach((t) => t.stop()); } catch (_) { /* gone */ }
    try { if (wakeLock) wakeLock.release(); } catch (_) { /* gone */ }
  }

  // ---- boot ------------------------------------------------------------------------------------------
  async function start() {
    const loading = $('loading');
    const fail = (msg) => { loading.textContent = msg; loading.classList.remove('gone'); };
    try { K.cfg = await call('/config'); }
    catch (e) {
      if (e.status === 401 || e.status === 403) { authLost(e.body.error); return; }
      fail('Cannot reach the server. Retrying…'); timers.push(setTimeout(() => { if (!stopped) start(); }, 5000)); return;
    }
    $('dev-name').textContent = K.cfg.device.name; $('dev-loc').textContent = K.cfg.device.location || '';
    document.title = `${K.cfg.device.name} · Fotile attendance kiosk`;
    ui.shadow(K.cfg.shadow);
    tickClock(); timers.push(setInterval(tickClock, 10000));
    try {
      K.faceapi = await loadFaceApi();
      K.engine = new FaceEngine(K.faceapi, browserEnv());
      await K.faceapi.tf.ready();
      await K.engine.load(MODEL_URL);
      await startCamera();
    } catch (e) { console.error(e); fail(e.name === 'NotAllowedError' ? 'Camera access was blocked. Allow the camera for this site, then reload.' : (e.message || 'Could not start the camera.')); return; }
    if (stopped) { cleanup(); return; }
    keepAwake();
    // heartbeat + refresh thresholds: HR can change them while the kiosk is running
    timers.push(setInterval(async () => {
      try { const c = await call('/config'); K.cfg = c; K.netDown = false; ui.shadow(c.shadow); if (K.mode !== 'challenge') ui.banner(K.slowNote || ''); }
      catch (e) { if (e.status === 0) ui.banner('Server unreachable — punches cannot be recorded'); else if (e.status === 401 || e.status === 403) authLost(e.body.error); }
    }, 60000));
    // warm up the networks so the first person does not wait, and measure how fast this device really is
    try { await K.engine.analyze(K.video, { expressions: true, quality: true, descriptor: true }); } catch (_) { /* no face yet is fine */ }
    try {
      const t0 = now();
      for (let i = 0; i < 3; i++) await K.engine.analyze(K.video, { expressions: true, quality: true });
      K.frameMs = (now() - t0) / 3;
      if (K.cfg.liveness.required && K.frameMs > 700) K.slowNote = `Slow device (${K.frameMs.toFixed(0)} ms per frame, ${K.faceapi.tf.getBackend()} backend). Liveness checks may fail — use a device with WebGL.`;
    } catch (_) { /* measuring is best effort */ }
    if (K.slowNote) ui.banner(K.slowNote);
    loading.classList.add('gone');
    K.running = true; K.mode = 'idle'; ui.mode('idle'); ui.say('Look at the camera', '');
    loop();
  }

  return {
    start,
    stop() { stopped = true; K.running = false; cleanup(); },
    openSetup,
  };
}
